import { useState, useEffect, useRef, useCallback } from 'react'
import { ExternalLink, Copy, Check, Loader2 } from 'lucide-react'

interface DevicePairingScreenProps {
  code: string
  pairBaseUrl: string
  onCancel?: () => void
}

const CHAR_DELAY_MS = 80
const HYPHEN_PAUSE_MS = 200

export function DevicePairingScreen({ code, pairBaseUrl, onCancel }: DevicePairingScreenProps) {
  const [displayedChars, setDisplayedChars] = useState(0)
  const [copied, setCopied] = useState(false)
  const timerRef = useRef<ReturnType<typeof setTimeout>>()

  const animationComplete = displayedChars >= code.length

  // Character-by-character typing animation
  useEffect(() => {
    if (displayedChars >= code.length) return

    const nextChar = code[displayedChars]
    const delay = nextChar === '-' ? HYPHEN_PAUSE_MS : CHAR_DELAY_MS

    timerRef.current = setTimeout(() => {
      setDisplayedChars((c) => c + 1)
    }, delay)

    return () => clearTimeout(timerRef.current)
  }, [displayedChars, code])

  const handleOpenBrowser = useCallback(() => {
    const url = `${pairBaseUrl}/pair#${code}`
    window.ledgerDesktop.openExternal(url)
  }, [pairBaseUrl, code])

  const handleCopy = useCallback(async () => {
    await navigator.clipboard.writeText(code)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }, [code])

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-slate-900 px-6">
      <div className="w-full max-w-md text-center">
        {/* Title */}
        <h1 className="mb-2 text-xl font-semibold text-white">Pair this device</h1>
        <p className="mb-8 text-sm text-slate-400">
          Enter the code below in your browser to authorize this device.
        </p>

        {/* Animated code display */}
        <div className="relative mb-8 flex items-center justify-center rounded-lg bg-slate-800 px-6 py-8">
          <span className="font-mono text-5xl font-bold tracking-[0.2em] text-white">
            {code.slice(0, displayedChars)}
          </span>
          {/* Blinking cursor */}
          {!animationComplete && (
            <span className="ml-0.5 inline-block h-12 w-0.5 animate-pulse bg-indigo-400" />
          )}
        </div>

        {/* Action buttons */}
        <div className="flex flex-col gap-3">
          <button
            type="button"
            onClick={handleOpenBrowser}
            disabled={!animationComplete}
            className={`flex items-center justify-center gap-2 rounded-lg px-4 py-3 text-sm font-medium transition-all ${
              animationComplete
                ? 'bg-indigo-600 text-white hover:bg-indigo-500'
                : 'cursor-not-allowed bg-slate-700 text-slate-500'
            }`}
          >
            <ExternalLink size={16} />
            Open Browser
          </button>

          <div className="flex gap-3">
            <button
              type="button"
              onClick={handleCopy}
              disabled={!animationComplete}
              className={`flex flex-1 items-center justify-center gap-2 rounded-lg px-4 py-2.5 text-sm font-medium transition-all ${
                animationComplete
                  ? 'bg-slate-700 text-slate-200 hover:bg-slate-600'
                  : 'cursor-not-allowed bg-slate-800 text-slate-600'
              }`}
            >
              {copied ? <Check size={14} className="text-emerald-400" /> : <Copy size={14} />}
              {copied ? 'Copied!' : 'Copy Code'}
            </button>

            {onCancel && (
              <button
                type="button"
                onClick={onCancel}
                className="rounded-lg px-4 py-2.5 text-sm font-medium text-slate-400 transition-colors hover:bg-slate-800 hover:text-slate-200"
              >
                Cancel
              </button>
            )}
          </div>
        </div>

        {/* Polling status */}
        <div className="mt-8 flex items-center justify-center gap-2 text-xs text-slate-500">
          <Loader2 size={12} className="animate-spin" />
          Waiting for approval...
        </div>
      </div>
    </div>
  )
}
